﻿<script setup lang="ts">
import { computed } from 'vue'
import type { CategoryRecord, Id } from '../types/catalog'
import AppIcon from './AppIcon.vue'
import CategoryNode from './CategoryNode.vue'

const props = defineProps<{
  categories: CategoryRecord[]
  selectedId: Id | null
}>()

const emit = defineEmits<{
  select: [id: Id | null]
  add: [parentId: Id | null]
  rename: [category: CategoryRecord]
  remove: [category: CategoryRecord]
}>()

const childrenMap = computed(() => {
  const map = new Map<Id | null, CategoryRecord[]>()
  for (const category of props.categories) {
    const children = map.get(category.parentId) ?? []
    children.push(category)
    map.set(category.parentId, children)
  }
  for (const children of map.values()) children.sort((a, b) => a.name.localeCompare(b.name, 'en'))
  return map
})

const roots = computed(() => childrenMap.value.get(null) ?? [])
</script>

<template>
  <nav class="category-tree" aria-label="Image categories">
    <div class="category-tree-title">
      <span>Categories</span>
      <button type="button" class="mini-icon-button" aria-label="New root category" @click="emit('add', null)">
        <AppIcon name="plus" :size="17" />
      </button>
    </div>
    <button
      type="button"
      class="category-all"
      :class="{ 'category-row--selected': selectedId === null }"
      @click="emit('select', null)"
    >
      <AppIcon name="all" :size="19" />
      <span>All images</span>
    </button>
    <ul class="category-root-list">
      <CategoryNode
        v-for="root in roots"
        :key="root.id"
        :node="root"
        :children-map="childrenMap"
        :selected-id="selectedId"
        @select="emit('select', $event)"
        @add="emit('add', $event)"
        @rename="emit('rename', $event)"
        @remove="emit('remove', $event)"
      />
    </ul>
  </nav>
</template>
